import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useUsers } from "@/hooks/useUsers";
import { useAllCompanies } from "@/hooks/useCompanies";
import { useRoles } from "@/hooks/useRoles";
import { UserRequest } from "@/types/api";
import { formatRole } from "@/lib/utils";

interface UserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (data: UserRequest) => void;
}

const UserModal = ({ open, onOpenChange, onSave }: UserModalProps) => {
  const [formData, setFormData] = useState<UserRequest>({
    name: "",
    email: "",
    password: "", // Mantemos no tipo, mas não será exibido no formulário
    companyId: "",
    rolesIdList: [],
  });

  const { createUser, isCreating } = useUsers();
  const { companies } = useAllCompanies();
  const { roles } = useRoles();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (onSave) {
      // Usa a função onSave passada via props (para casos customizados)
      onSave(formData);
    } else {
      // Usa a integração padrão com o backend
      createUser(formData, {
        onSuccess: () => {
          onOpenChange(false);
          setFormData({
            name: "",
            email: "",
            companyId: "",
            password: "", // Mantemos vazio, mas não será usado
            rolesIdList: [],
          });
        },
      });
    }
  };

  const handleInputChange = (field: keyof UserRequest, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRoleChange = (roleId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      rolesIdList: checked
        ? [...prev.rolesIdList, roleId]
        : prev.rolesIdList.filter(id => id !== roleId)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Novo Usuário</DialogTitle>
          <DialogDescription>
            Preencha os dados para criar um novo usuário.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Nome completo"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="email@exemplo.com"
              required
            />
          </div>

          <div className="space-y-2 p-3 bg-blue-50 rounded-md border border-blue-200">
            <div className="flex items-center text-blue-700">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <span className="font-medium">Informação sobre senha</span>
            </div>
            <p className="text-sm text-blue-600 mt-1">
              Uma senha temporária será gerada automaticamente e enviada para o email do usuário.
              O usuário será solicitado a alterar a senha no primeiro acesso.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company">Empresa</Label>
            <Select
              value={formData.companyId}
              onValueChange={(value) => handleInputChange("companyId", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma empresa" />
              </SelectTrigger>
              <SelectContent>
                {companies?.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Roles</Label>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
              {roles?.map((role) => (
                <div key={role.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={role.id}
                    checked={formData.rolesIdList.includes(role.id)}
                    onCheckedChange={(checked) => handleRoleChange(role.id, checked as boolean)}
                  />
                  <Label htmlFor={role.id} className="text-sm">
                    {formatRole(role.name)}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isCreating}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? "Criando..." : "Criar Usuário"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserModal;
